import React, { useState, useCallback } from 'react';
import { salesforceImportService } from '../services/salesforceImportService';
import type { NormalizedSalesforceImport } from '../types/salesforceTypes';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Card, CardContent, CardHeader } from '../../../foundation/altzero_base/base/components/ui/card';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { ENERGY_RATE_STEP, ENV_RATE_STEP } from '../lib/ratePrecision';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Textarea } from '../../../foundation/altzero_base/base/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../foundation/altzero_base/base/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../foundation/altzero_base/base/components/ui/tabs';
import {
  ChevronDown,
  ChevronUp,
  Trash2,
  Copy,
  Edit,
  MapPin,
  Zap,
  Building2,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Settings,
  Upload,
  File,
  X,
  Loader2,
  Cloud,
} from 'lucide-react';
import { Site, validateSite, AUSTRALIAN_STATES, RATE_TYPES, ENERGY_RETAILERS, METER_PROVIDERS, DMA_PROVIDERS, RETAILER_COMMS, formatNMI, validatePostcodeForState, CurrentContractRates, createEmptyCurrentContractRates } from '../types/siteTypes';
import { getSchemesForState } from '../lib/envSchemeRegistry';
import { uploadSiteDocument, deleteSiteDocument, getSiteDocumentUrl, updateSite } from '../services/siteService';
import { getUserOrganizationId } from '../services/tenderApiService';

interface SiteCardProps {
  site: Site;
  index: number;
  isExpanded: boolean;
  onUpdate: (site: Site) => void;
  /**
   * The Salesforce tender owner this site's import carried, when it carried one.
   *
   * ⚠️ A PER-SITE IMPORT USED TO DROP IT ENTIRELY. The top-level import seeds Broker Details
   * from the owner; this one mapped site fields only, so on a multi-site tender — where each
   * site is imported from its own card — Broker Details kept the signed-in user's profile
   * pre-fill and the proposal went out with the wrong person's contact details.
   */
  onSalesforceConsultant?: (consultant: NormalizedSalesforceImport['consultant']) => void;
  onRemove: (siteId: string) => void;
  onCopy: (siteId: string) => void;
  onToggleExpansion: (siteId: string) => void;
  showErrors?: boolean;
}

const SiteCard: React.FC<SiteCardProps> = ({
  site,
  index,
  isExpanded,
  onUpdate,
  onSalesforceConsultant,
  onRemove,
  onCopy,
  onToggleExpansion,
  showErrors = false,
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [deletingFiles, setDeletingFiles] = useState<string[]>([]);

  // Per-site Salesforce import — each site is its own Opportunity/Tender__c in
  // Salesforce (1 Opportunity = 1 site), so a multi-site tender needs one import
  // per site row, not one shared import applied to "site 1" only.
  const [sfImportOpen, setSfImportOpen] = useState(false);
  const [sfOppIdInput, setSfOppIdInput] = useState('');
  const [sfImporting, setSfImporting] = useState(false);
  const [sfImportError, setSfImportError] = useState<string | null>(null);
  const [sfImportSuccess, setSfImportSuccess] = useState(false);
  const validation = validateSite(site);
  const isValid = validation.isValid;
  // Only show red borders after user has attempted to continue
  const e = (hasError: string | undefined) => showErrors && !!hasError;

  // Calculate total usage
  const calculateTotalUsage = (peak?: number, offpeak?: number, shoulder?: number): number => {
    const peakVal = peak || 0;
    const offpeakVal = offpeak || 0;
    const shoulderVal = shoulder || 0;
    return peakVal + offpeakVal + shoulderVal;
  };

  const totalUsage = site.annualKwhTotal ?? calculateTotalUsage(site.annualKwhPeak, site.annualKwhOffpeak, site.annualKwhShoulder);

  // Update site field - memoized to prevent input focus loss
  const updateField = useCallback((field: string, value: any) => {
    const updatedSite = { ...site };

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updatedSite[parent as keyof Site] = {
        ...updatedSite[parent as keyof Site],
        [child]: value,
      };
    } else {
      updatedSite[field as keyof Site] = value;
    }

    // Auto-calculate total usage when peak, off-peak, or shoulder changes
    if (field === 'annualKwhPeak' || field === 'annualKwhOffpeak' || field === 'annualKwhShoulder') {
      const peak = field === 'annualKwhPeak' ? (value || 0) : (updatedSite.annualKwhPeak || 0);
      const offpeak = field === 'annualKwhOffpeak' ? (value || 0) : (updatedSite.annualKwhOffpeak || 0);
      const shoulder = field === 'annualKwhShoulder' ? (value || 0) : (updatedSite.annualKwhShoulder || 0);
      const total = peak + offpeak + shoulder;
      updatedSite.annualKwhTotal = total > 0 ? total : undefined;
    }

    // Mark as not new once user starts editing
    if (site.isNew && value) {
      updatedSite.isNew = false;
    }

    onUpdate(updatedSite);
  }, [site, onUpdate]);

  // Import this ONE site's fields from its own Salesforce Opportunity.
  // Salesforce models 1 Opportunity (+ its linked Tender__c) = 1 site, so a
  // multi-site tender needs one import per site row — this button targets
  // only the site it's on, unlike the tender-level import field (Client Info)
  // which only ever filled site #1.
  const handleImportFromSalesforce = useCallback(async () => {
    const oppId = sfOppIdInput.trim();
    if (!oppId) return;
    setSfImporting(true);
    setSfImportError(null);
    setSfImportSuccess(false);
    try {
      const result = await salesforceImportService.importFromOpportunity(oppId, 'opportunity');
      if (!result.success || !result.data) {
        setSfImportError(result.error || 'Import failed');
        return;
      }
      const normalized = salesforceImportService.normalizePayload(result.data);

      const updatedSite: Site = {
        ...site,
        ...(normalized.entityName ? { name: normalized.entityName } : {}),
        ...(normalized.nmi ? { nmi: normalized.nmi } : {}),
        ...(normalized.abn ? { abn: normalized.abn } : {}),
        ...(normalized.acn ? { acn: normalized.acn } : {}),
        ...(normalized.annualKwhPeak !== null ? { annualKwhPeak: normalized.annualKwhPeak } : {}),
        ...(normalized.annualKwhOffpeak !== null ? { annualKwhOffpeak: normalized.annualKwhOffpeak } : {}),
        ...(normalized.annualKwhShoulder !== null ? { annualKwhShoulder: normalized.annualKwhShoulder } : {}),
        ...(normalized.annualKwhTotal !== null ? { annualKwhTotal: normalized.annualKwhTotal } : {}),
        ...(normalized.currentContractExpiry !== null ? { currentContractExpiry: normalized.currentContractExpiry } : {}),
        ...(normalized.currentMeterProvider ? { currentMeterProvider: normalized.currentMeterProvider } : {}),
        ...(normalized.currentRetailer ? { currentRetailer: normalized.currentRetailer } : {}),
        ...(normalized.dmaProvider ? { dmaProvider: normalized.dmaProvider } : {}),
        ...(normalized.retailerComms ? { retailerComms: normalized.retailerComms } : {}),
        address: {
          ...site.address,
          ...(normalized.address.street ? { street: normalized.address.street } : {}),
          ...(normalized.address.city ? { city: normalized.address.city } : {}),
          ...(normalized.address.state ? { state: normalized.address.state } : {}),
          ...(normalized.address.postalCode ? { postcode: normalized.address.postalCode } : {}),
          ...(normalized.address.country ? { country: normalized.address.country } : {}),
        },
        isNew: false,
      };
      onUpdate(updatedSite);
      // Hand the owner up before the panel closes — the parent decides whether it may overwrite
      // what is already in Broker Details.
      if (normalized.consultant) onSalesforceConsultant?.(normalized.consultant);
      setSfImportSuccess(true);
      setTimeout(() => { setSfImportOpen(false); setSfImportSuccess(false); setSfOppIdInput(''); }, 1200);
    } catch (err) {
      setSfImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setSfImporting(false);
    }
  }, [sfOppIdInput, site, onUpdate, onSalesforceConsultant]);

  // Update a nested field inside currentContractRates (3-level deep: category → field → value)
  const updateCurrentContractRate = useCallback((category: keyof CurrentContractRates, field: string, value: number) => {
    const existing = site.currentContractRates || createEmptyCurrentContractRates();
    const updatedSite: Site = {
      ...site,
      currentContractRates: {
        ...existing,
        [category]: {
          ...(existing[category] as Record<string, number>),
          [field]: value,
        },
      },
    };
    onUpdate(updatedSite);
  }, [site, onUpdate]);

  // Get display info for collapsed state
  const getDisplayInfo = () => {
    const parts = [];
    if (site.nmi) parts.push(`NMI: ${site.nmi}`);
    if (site.address.city) parts.push(site.address.city);
    if (site.address.state) parts.push(site.address.state);
    return parts.join(' • ') || 'No details added';
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    const orgId = site.orgId || await getUserOrganizationId();
    if (!orgId) {
      alert('Organization ID not found. Please ensure you are logged in.');
      return;
    }

    const fileNames = fileArray.map(f => f.name);
    setUploadingFiles(fileNames);

    try {
      const uploadPromises = fileArray.map(async (file) => {
        const result = await uploadSiteDocument(file, site.id, orgId);
        if (!result.success) {
          throw new Error(result.error || 'Upload failed');
        }
        return result.filePath!;
      });

      const uploadedPaths = await Promise.all(uploadPromises);
      const currentPaths = site.documentFilePaths || [];
      const updatedPaths = [...currentPaths, ...uploadedPaths];

      // Update local state
      updateField('documentFilePaths', updatedPaths);

      // Persist to DB using the global site ID (siteId), not the tender_site junction ID (id)
      const globalSiteId = site.siteId;
      if (globalSiteId && !globalSiteId.startsWith('site_')) {
        try {
          const updateResult = await updateSite(globalSiteId, { documentFilePaths: updatedPaths });
          if (!updateResult.success) {
            console.error('Failed to update site document paths:', updateResult.error);
          }
        } catch (error) {
          console.error('Error updating site document paths:', error);
        }
      }

      setUploadingFiles([]);
    } catch (error: any) {
      console.error('Error uploading files:', error);
      alert(`Upload failed: ${error.message}`);
      setUploadingFiles([]);
    }
  };

  // Handle file delete
  const handleFileDelete = async (filePath: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    setDeletingFiles([...deletingFiles, filePath]);

    try {
      const result = await deleteSiteDocument(filePath);
      if (result.success) {
        const currentPaths = site.documentFilePaths || [];
        const updatedPaths = currentPaths.filter(path => path !== filePath);

        // Update local state
        updateField('documentFilePaths', updatedPaths);

        // Persist to DB using the global site ID (siteId), not the tender_site junction ID (id)
        const globalSiteId = site.siteId;
        if (globalSiteId && !globalSiteId.startsWith('site_')) {
          try {
            const updateResult = await updateSite(globalSiteId, { documentFilePaths: updatedPaths });
            if (!updateResult.success) {
              console.error('Failed to update site document paths:', updateResult.error);
            }
          } catch (error) {
            console.error('Error updating site document paths:', error);
          }
        }
      } else {
        alert(`Delete failed: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert(`Delete failed: ${error.message}`);
    } finally {
      setDeletingFiles(deletingFiles.filter(path => path !== filePath));
    }
  };

  // Get file name from path
  const getFileName = (filePath: string): string => {
    return filePath.split('/').pop() || filePath;
  };

  return (
    <Card className={`transition-all duration-300 ${isValid
      ? 'border-green-200'
      : site.isNew
        ? 'border-blue-200 bg-blue-50/30'
        : 'border-amber-200 bg-amber-50/30'
      } hover:shadow-md`}>
      {/* Card Header - Always Visible */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className={`p-2 rounded-lg ${isValid ? 'bg-green-100' : site.isNew ? 'bg-blue-100' : 'bg-amber-100'
              }`}>
              <Building2 className={`w-4 h-4 ${isValid ? 'text-green-600' : site.isNew ? 'text-blue-600' : 'text-amber-600'
                }`} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900 truncate">
                  {site.name || `Site ${index + 1}`}
                </h3>
                <Badge variant={isValid ? 'default' : 'secondary'} className="text-xs">
                  {isValid ? (
                    <><CheckCircle className="w-3 h-3 mr-1" />Complete</>
                  ) : (
                    <><AlertCircle className="w-3 h-3 mr-1" />Incomplete</>
                  )}
                </Badge>
              </div>

              {!isExpanded && (
                <p className="text-sm text-gray-500 truncate mt-1">
                  {getDisplayInfo()}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSfImportOpen((v) => !v)}
              className="text-gray-500 hover:text-gray-700"
              title="Import this site from Salesforce (Opportunity ID)"
            >
              <Cloud className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCopy(site.id)}
              className="text-gray-500 hover:text-gray-700"
              title="Copy Site"
            >
              <Copy className="w-4 h-4" />
            </Button>

            {index > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // ⚠️ offer.tender_site_id is ON DELETE CASCADE and every offer on prod
                  // carries one, so this takes the site's offers with it. Ask first, the same
                  // way OfferManagementCard asks before deleting an offer.
                  const label = site.name?.trim() || site.nmi || 'this site';
                  if (window.confirm(`Remove ${label}? Any offers priced for it will be deleted too. This cannot be undone.`)) {
                    onRemove(site.id);
                  }
                }}
                className="text-red-500 hover:text-red-700"
                title="Remove Site"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleExpansion(site.id)}
              className="text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {sfImportOpen && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2">
            <Cloud className="w-4 h-4 text-blue-500 shrink-0" />
            <input
              type="text"
              value={sfOppIdInput}
              onChange={(ev) => setSfOppIdInput(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') void handleImportFromSalesforce(); }}
              placeholder="Salesforce Opportunity ID for this site"
              className="flex-1 h-8 rounded-md border border-gray-200 bg-white px-2 text-xs"
              disabled={sfImporting}
            />
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={sfImporting || !sfOppIdInput.trim()}
              onClick={() => void handleImportFromSalesforce()}
            >
              {sfImporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : sfImportSuccess ? 'Imported ✓' : 'Import'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-gray-500"
              onClick={() => { setSfImportOpen(false); setSfImportError(null); }}
            >
              Cancel
            </Button>
            {sfImportError && (
              <span className="text-xs text-red-600 shrink-0">{sfImportError}</span>
            )}
          </div>
        )}
      </CardHeader>

      {/* Expandable Content */}
      {isExpanded && (
        <CardContent className="pt-0 animate-in slide-in-from-top-2 duration-300">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="basic" className="relative">
                Basic Info
                {showErrors && (validation.errors.name || validation.errors.nmi) && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </TabsTrigger>
              <TabsTrigger value="address" className="relative">
                Address
                {showErrors && validation.errors.address?.street && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </TabsTrigger>
              <TabsTrigger value="energy" className="relative">
                Energy Details
                {showErrors && (validation.errors.annualKwhPeak || validation.errors.annualKwhOffpeak || validation.errors.energyRates?.peak_rate || validation.errors.energyRates?.offpeak_rate) && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>

            {/* Basic Information Tab */}
            <TabsContent value="basic" className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-gray-900">Site Information</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-name-${site.id}`} className="text-sm font-medium">
                      Site Name <span className="text-red-500">*</span>
                    </Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.name) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input
                        id={`site-name-${site.id}`}
                        placeholder="e.g., Head Office, Warehouse 1"
                        value={site.name}
                        onChange={(ev) => updateField('name', ev.target.value)}
                        className={e(validation.errors.name) ? 'border-red-500' : ''}
                      />
                    </div>
                    {e(validation.errors.name) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.name}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`site-nmi-${site.id}`} className="text-sm font-medium">
                      NMI <span className="text-red-500">*</span>
                    </Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.nmi) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input
                        id={`site-nmi-${site.id}`}
                        placeholder="enter the NMI"
                        value={site.nmi}
                        onChange={(ev) => updateField('nmi', formatNMI(ev.target.value))}
                        className={e(validation.errors.nmi) ? 'border-red-500' : ''}
                        maxLength={11}
                      />
                    </div>
                    {e(validation.errors.nmi) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.nmi}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">National Meter Identifier</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-abn-${site.id}`} className="text-sm font-medium">
                      ABN (Optional)
                    </Label>
                    <Input
                      id={`site-abn-${site.id}`}
                      placeholder="11 digit ABN"
                      value={site.abn || ''}
                      onChange={(e) => updateField('abn', e.target.value)}
                      className={`mt-1 ${e(validation.errors.abn) ? 'border-red-300' : ''}`}
                      maxLength={11}
                    />
                    {e(validation.errors.abn) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.abn}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`site-acn-${site.id}`} className="text-sm font-medium">
                      ACN (Optional)
                    </Label>
                    <Input
                      id={`site-acn-${site.id}`}
                      placeholder="9 digit ACN"
                      value={site.acn || ''}
                      onChange={(e) => updateField('acn', e.target.value)}
                      className={`mt-1 ${e(validation.errors.acn) ? 'border-red-300' : ''}`}
                      maxLength={9}
                    />
                    {e(validation.errors.acn) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.acn}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-gray-900">Current Provider Details</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-retailer-${site.id}`} className="text-sm font-medium">
                      Current Retailer
                    </Label>
                    <Select
                      value={site.currentRetailer || ''}
                      onValueChange={(value) => updateField('currentRetailer', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select current retailer" />
                      </SelectTrigger>
                      <SelectContent>
                        {ENERGY_RETAILERS.map((retailer) => (
                          <SelectItem key={retailer} value={retailer}>
                            {retailer}
                          </SelectItem>
                        ))}
                        {site.currentRetailer && !ENERGY_RETAILERS.includes(site.currentRetailer) && (
                          <SelectItem key={site.currentRetailer} value={site.currentRetailer}>
                            {site.currentRetailer}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor={`site-meter-provider-${site.id}`} className="text-sm font-medium">
                      Current Meter Provider
                    </Label>
                    <Select
                      value={site.currentMeterProvider || ''}
                      onValueChange={(value) => updateField('currentMeterProvider', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select meter provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {METER_PROVIDERS.map((provider) => (
                          <SelectItem key={provider} value={provider}>
                            {provider}
                          </SelectItem>
                        ))}
                        {site.currentMeterProvider && !METER_PROVIDERS.includes(site.currentMeterProvider) && (
                          <SelectItem key={site.currentMeterProvider} value={site.currentMeterProvider}>
                            {site.currentMeterProvider}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-retailer-comms-${site.id}`} className="text-sm font-medium">
                      Retailer Comms
                    </Label>
                    <Select
                      value={site.retailerComms || ''}
                      onValueChange={(value) => updateField('retailerComms', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select retailer commission" />
                      </SelectTrigger>
                      <SelectContent>
                        {RETAILER_COMMS.map((comm) => (
                          <SelectItem key={comm} value={comm}>
                            {comm}
                          </SelectItem>
                        ))}
                        {site.retailerComms && !RETAILER_COMMS.includes(site.retailerComms) && (
                          <SelectItem key={site.retailerComms} value={site.retailerComms}>
                            {site.retailerComms}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-contract-expiry-${site.id}`} className="text-sm font-medium">
                      Current Contract Expiry
                    </Label>
                    <Input
                      id={`site-contract-expiry-${site.id}`}
                      type="date"
                      value={site.currentContractExpiry ? new Date(site.currentContractExpiry).toISOString().split('T')[0] : ''}
                      onChange={(e) => updateField('currentContractExpiry', e.target.value ? new Date(e.target.value) : undefined)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor={`site-due-at-${site.id}`} className="text-sm font-medium">
                      Due Date
                    </Label>
                    <Input
                      id={`site-due-at-${site.id}`}
                      type="datetime-local"
                      value={site.dueAt ? new Date(site.dueAt).toISOString().slice(0, 16) : ''}
                      onChange={(e) => updateField('dueAt', e.target.value ? new Date(e.target.value) : undefined)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Address Tab */}
            <TabsContent value="address" className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-gray-900">Site Address</h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor={`site-street-${site.id}`} className="text-sm font-medium">
                      Street Address <span className="text-red-500">*</span>
                    </Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.address?.street) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input
                        id={`site-street-${site.id}`}
                        placeholder="123 Main Street"
                        value={site.address.street}
                        onChange={(ev) => updateField('address.street', ev.target.value)}
                        className={e(validation.errors.address?.street) ? 'border-red-500' : ''}
                      />
                    </div>
                    {e(validation.errors.address?.street) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.address!.street}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor={`site-city-${site.id}`} className="text-sm font-medium">
                        City
                      </Label>
                      <Input
                        id={`site-city-${site.id}`}
                        placeholder="Sydney"
                        value={site.address.city}
                        onChange={(e) => updateField('address.city', e.target.value)}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor={`site-state-${site.id}`} className="text-sm font-medium">
                        State
                      </Label>
                      <Select
                        value={site.address.state}
                        onValueChange={(value) => updateField('address.state', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {AUSTRALIAN_STATES.map((state) => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor={`site-postcode-${site.id}`} className="text-sm font-medium">
                        Postcode
                      </Label>
                      <Input
                        id={`site-postcode-${site.id}`}
                        placeholder="2000"
                        value={site.address.postcode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          updateField('address.postcode', value);
                        }}
                        className="mt-1"
                        maxLength={4}
                      />
                      {site.address.postcode && site.address.state &&
                        !validatePostcodeForState(site.address.postcode, site.address.state) && (
                          <p className="text-amber-600 text-xs mt-1">
                            Postcode may not match selected state
                          </p>
                        )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor={`site-country-${site.id}`} className="text-sm font-medium">
                      Country
                    </Label>
                    <Input
                      id={`site-country-${site.id}`}
                      value={site.address.country}
                      onChange={(e) => updateField('address.country', e.target.value)}
                      className="mt-1"
                      disabled
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Energy Details Tab */}
            <TabsContent value="energy" className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-gray-900">Energy Usage & Details</h4>
                </div>

                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-expiry-${site.id}`} className="text-sm font-medium">
                      Offer Expiry <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`site-expiry-${site.id}`}
                      type="date"
                      value={site.energyDetails.offerExpiry}
                      onChange={(e) => updateField('energyDetails.offerExpiry', e.target.value)}
                      className={`mt-1 ${validation.errors.energyDetails?.offerExpiry ? 'border-red-300' : ''}`}
                    />
                    {validation.errors.energyDetails?.offerExpiry && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.energyDetails.offerExpiry}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`site-rate-type-${site.id}`} className="text-sm font-medium">
                      Rate Type
                    </Label>
                    <Select
                      value={site.energyDetails.rateType}
                      onValueChange={(value) => updateField('energyDetails.rateType', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select rate type" />
                      </SelectTrigger>
                      <SelectContent>
                        {RATE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div> */}

                {/* <div className="pt-4">
                  <h5 className="font-medium mb-3">Current Energy Rates</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor={`site-peak-rate-${site.id}`} className="text-sm font-medium">
                        Peak Rate (c/kWh) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`site-peak-rate-${site.id}`}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="25.50"
                        value={site.energyDetails.peakRate || ''}
                        onChange={(e) => updateField('energyDetails.peakRate', parseFloat(e.target.value) || 0)}
                        className={`mt-1 ${validation.errors.energyDetails?.peakRate ? 'border-red-300' : ''}`}
                      />
                      {validation.errors.energyDetails?.peakRate && (
                        <p className="text-red-600 text-xs mt-1">{validation.errors.energyDetails.peakRate}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor={`site-offpeak-rate-${site.id}`} className="text-sm font-medium">
                        Off-Peak Rate (c/kWh)
                      </Label>
                      <Input
                        id={`site-offpeak-rate-${site.id}`}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="18.50"
                        value={site.energyDetails.offpeakRate || ''}
                        onChange={(e) => updateField('energyDetails.offpeakRate', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor={`site-shoulder-rate-${site.id}`} className="text-sm font-medium">
                        Shoulder Rate (c/kWh)
                      </Label>
                      <Input
                        id={`site-shoulder-rate-${site.id}`}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="22.00"
                        value={site.energyDetails.shoulderRate || ''}
                        onChange={(e) => updateField('energyDetails.shoulderRate', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div> */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-daily-charge-${site.id}`} className="text-sm font-medium">
                      Daily Charges ($/day)
                    </Label>
                    <Input
                      id={`site-daily-charge-${site.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.95"
                      value={site.dailyCharges || ''}
                      onChange={(e) => updateField('dailyCharges', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor={`site-non-energy-${site.id}`} className="text-sm font-medium">
                      Non-Energy Charges ($)
                    </Label>
                    <Input
                      id={`site-non-energy-${site.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="15.00"
                      value={site.nonEnergyCharges || ''}
                      onChange={(e) => updateField('nonEnergyCharges', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-3">Annual Usage (kWh) <span className="text-red-500 text-xs font-normal ml-1">* required for calculation</span></h5>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor={`site-annual-peak-${site.id}`} className="text-sm font-medium">
                      Peak Usage <span className="text-red-500">*</span>
                    </Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.annualKwhPeak) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input
                        id={`site-annual-peak-${site.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="50000"
                        value={site.annualKwhPeak || ''}
                        onChange={(ev) => updateField('annualKwhPeak', ev.target.value ? parseFloat(ev.target.value) : undefined)}
                        className={e(validation.errors.annualKwhPeak) ? 'border-red-500' : ''}
                      />
                    </div>
                    {e(validation.errors.annualKwhPeak) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.annualKwhPeak}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`site-annual-offpeak-${site.id}`} className="text-sm font-medium">
                      Off-Peak Usage <span className="text-red-500">*</span>
                    </Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.annualKwhOffpeak) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input
                        id={`site-annual-offpeak-${site.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="30000"
                        value={site.annualKwhOffpeak || ''}
                        onChange={(ev) => updateField('annualKwhOffpeak', ev.target.value ? parseFloat(ev.target.value) : undefined)}
                        className={e(validation.errors.annualKwhOffpeak) ? 'border-red-500' : ''}
                      />
                    </div>
                    {e(validation.errors.annualKwhOffpeak) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.annualKwhOffpeak}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`site-annual-shoulder-${site.id}`} className="text-sm font-medium">
                      Shoulder Usage
                    </Label>
                    <Input
                      id={`site-annual-shoulder-${site.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="20000"
                      value={site.annualKwhShoulder || ''}
                      onChange={(e) => updateField('annualKwhShoulder', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className={`mt-1 ${e(validation.errors.annualKwhShoulder) ? 'border-red-300' : ''}`}
                    />
                    {e(validation.errors.annualKwhShoulder) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.annualKwhShoulder}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`site-annual-total-${site.id}`} className="text-sm font-medium">
                      Total Usage <span className="text-gray-400 font-normal">(Auto)</span>
                    </Label>
                    <Input
                      id={`site-annual-total-${site.id}`}
                      type="number"
                      readOnly
                      value={totalUsage > 0 ? totalUsage : ''}
                      className="mt-1 bg-gray-50 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-gray-500 mt-1">Sum of Peak, Off-Peak & Shoulder</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h5 className="font-medium mb-3">Peak Demand</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`site-peak-demand-kw-${site.id}`} className="text-sm font-medium">
                      Peak Demand (kW)
                    </Label>
                    <Input
                      id={`site-peak-demand-kw-${site.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="150"
                      value={site.peakDemandKw || ''}
                      onChange={(e) => updateField('peakDemandKw', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum demand in kilowatts</p>
                  </div>

                  <div>
                    <Label htmlFor={`site-peak-demand-kva-${site.id}`} className="text-sm font-medium">
                      Peak Demand (kVA) <span className="text-gray-400 font-normal">(Optional)</span>
                    </Label>
                    <Input
                      id={`site-peak-demand-kva-${site.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="165"
                      value={site.peakDemandKva || ''}
                      onChange={(e) => updateField('peakDemandKva', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum demand in kilovolt-amperes</p>
                  </div>
                </div>
              </div>

              {/* Current Contract — Energy Rates */}
              <div className="pt-4 border-t">
                <h5 className="font-medium mb-1">Current Contract — Energy Rates <span className="text-gray-400 text-xs font-normal ml-1">(optional — leave blank for new clients)</span></h5>
                <p className="text-xs text-gray-500 mb-3">Rates from current retailer contract (c/kWh or c/day)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Peak Rate (c/kWh)</Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.energyRates?.peak_rate) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input type="number" step={ENERGY_RATE_STEP} min="0"
                        className={e(validation.errors.energyRates?.peak_rate) ? 'border-red-500' : ''}
                        placeholder="e.g. 29.51"
                        value={site.currentContractRates?.energy?.peak_rate || ''}
                        onChange={(ev) => updateCurrentContractRate('energy', 'peak_rate', parseFloat(ev.target.value) || 0)} />
                    </div>
                    {e(validation.errors.energyRates?.peak_rate) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.energyRates!.peak_rate}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Off-Peak Rate (c/kWh)</Label>
                    <div className={`mt-1 rounded-md ${e(validation.errors.energyRates?.offpeak_rate) ? 'ring-2 ring-red-500' : ''}`}>
                      <Input type="number" step={ENERGY_RATE_STEP} min="0"
                        className={e(validation.errors.energyRates?.offpeak_rate) ? 'border-red-500' : ''}
                        placeholder="e.g. 14.76"
                        value={site.currentContractRates?.energy?.offpeak_rate || ''}
                        onChange={(ev) => updateCurrentContractRate('energy', 'offpeak_rate', parseFloat(ev.target.value) || 0)} />
                    </div>
                    {e(validation.errors.energyRates?.offpeak_rate) && (
                      <p className="text-red-600 text-xs mt-1">{validation.errors.energyRates!.offpeak_rate}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Shoulder Rate (c/kWh)</Label>
                    <Input type="number" step={ENERGY_RATE_STEP} min="0" className="mt-1"
                      placeholder="e.g. 15.81"
                      value={site.currentContractRates?.energy?.shoulder_rate || ''}
                      onChange={(e) => updateCurrentContractRate('energy', 'shoulder_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Daily Supply (c/day)</Label>
                    <Input type="number" step="0.0001" min="0" className="mt-1"
                      placeholder="e.g. 24.00"
                      value={site.currentContractRates?.energy?.daily_supply_rate || ''}
                      onChange={(e) => updateCurrentContractRate('energy', 'daily_supply_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
              </div>

              {/* Current Contract — Network Charges */}
              <div className="pt-4 border-t">
                <h5 className="font-medium mb-1">Current Contract — Network Charges</h5>
                <p className="text-xs text-gray-500 mb-3">Distributor pass-through charges</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Winter Peak (c/kWh)</Label>
                    <Input type="number" step={ENERGY_RATE_STEP} min="0" className="mt-1"
                      placeholder="e.g. 8.04"
                      value={site.currentContractRates?.network?.winter_peak_rate || ''}
                      onChange={(e) => updateCurrentContractRate('network', 'winter_peak_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Winter Off-Peak (c/kWh)</Label>
                    <Input type="number" step={ENERGY_RATE_STEP} min="0" className="mt-1"
                      placeholder="e.g. 2.71"
                      value={site.currentContractRates?.network?.winter_offpeak_rate || ''}
                      onChange={(e) => updateCurrentContractRate('network', 'winter_offpeak_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Capacity (c/kW/yr)</Label>
                    <Input type="number" step="0.01" min="0" className="mt-1"
                      placeholder="e.g. 409.53"
                      value={site.currentContractRates?.network?.capacity_rate || ''}
                      onChange={(e) => updateCurrentContractRate('network', 'capacity_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Supply Charge (c/site/yr)</Label>
                    <Input type="number" step="0.01" min="0" className="mt-1"
                      placeholder="e.g. 673.52"
                      value={site.currentContractRates?.network?.supply_charge_rate || ''}
                      onChange={(e) => updateCurrentContractRate('network', 'supply_charge_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
              </div>

              {/* Current Contract — Environmental Charges */}
              {(() => {
                // Registry-driven — schemes supported by CurrentContractEnvironmentalRates.
                // ESS folds into the VEEC row (VEEC/ESS/REES is one bucket); PRC isn't collected here.
                const STEP1_SCHEME_KEYS = ['veec', 'lrec', 'sres', 'esc', 'reps', 'eeis'];
                const schemes = getSchemesForState(site.address?.state).filter(s => STEP1_SCHEME_KEYS.includes(s.key));
                // Site rate fields use 'srec' for the SREC scheme (registry short key is 'sres')
                const fieldKey = (key: string) => (key === 'sres' ? 'srec' : key);
                return (
                  <div className="pt-4 border-t">
                    <h5 className="font-medium mb-1">Current Contract — Environmental Charges</h5>
                    <p className="text-xs text-gray-500 mb-3">{schemes.map(s => s.label).join(' / ')} rates and obligation %</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {schemes.map(s => (
                        <div key={`${s.key}-rate`}>
                          <Label className="text-xs font-medium">{s.label} Rate (c/kWh)</Label>
                          <Input type="number" step={ENV_RATE_STEP} min="0" className="mt-1"
                            placeholder="e.g. 0.9234"
                            value={(site.currentContractRates?.environmental as any)?.[`${fieldKey(s.key)}_rate`] ?? ''}
                            onChange={(ev) => updateCurrentContractRate('environmental', `${fieldKey(s.key)}_rate`, parseFloat(ev.target.value) || 0)} />
                        </div>
                      ))}
                      {schemes.map(s => (
                        <div key={`${s.key}-pct`}>
                          <Label className="text-xs font-medium">{s.label} Obligation %</Label>
                          <Input type="number" step="0.01" min="0" max="100" className="mt-1"
                            placeholder="e.g. 7.32"
                            value={(site.currentContractRates?.environmental as any)?.[`${fieldKey(s.key)}_power_percentage`] ?? ''}
                            onChange={(e) => updateCurrentContractRate('environmental', `${fieldKey(s.key)}_power_percentage`, parseFloat(e.target.value) || 0)} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Current Contract — Other Charges */}
              <div className="pt-4 border-t">
                <h5 className="font-medium mb-1">Current Contract — Other Charges</h5>
                <p className="text-xs text-gray-500 mb-3">Meter, service and AEMO charges</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Meter Supply (c/kWh)</Label>
                    <Input type="number" step="0.0001" min="0" className="mt-1"
                      placeholder="e.g. 8.04"
                      value={site.currentContractRates?.other?.meter_supply_rate || ''}
                      onChange={(e) => updateCurrentContractRate('other', 'meter_supply_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Service Charge (c/kWh)</Label>
                    <Input type="number" step="0.0001" min="0" className="mt-1"
                      placeholder="e.g. 2.71"
                      value={site.currentContractRates?.other?.service_charge_rate || ''}
                      onChange={(e) => updateCurrentContractRate('other', 'service_charge_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">AEMO Participant (c/kW/yr)</Label>
                    <Input type="number" step="0.01" min="0" className="mt-1"
                      placeholder="e.g. 409.53"
                      value={site.currentContractRates?.other?.aemo_participant_rate || ''}
                      onChange={(e) => updateCurrentContractRate('other', 'aemo_participant_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">AEMO Ancillary (c/site/yr)</Label>
                    <Input type="number" step="0.01" min="0" className="mt-1"
                      placeholder="e.g. 673.52"
                      value={site.currentContractRates?.other?.aemo_ancillary_rate || ''}
                      onChange={(e) => updateCurrentContractRate('other', 'aemo_ancillary_rate', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Advanced Tab */}
            <TabsContent value="advanced" className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-gray-900">Advanced Settings & Data Management</h4>
                </div>

                <p className="text-gray-600 mb-4">
                  Configure data management and communication preferences for this site.
                </p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`site-create-dma-${site.id}`}
                        checked={site.createDma}
                        onChange={(e) => updateField('createDma', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`site-create-dma-${site.id}`} className="text-sm">
                        Create DMA
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`site-request-meter-${site.id}`}
                        checked={site.requestMeterData}
                        onChange={(e) => updateField('requestMeterData', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`site-request-meter-${site.id}`} className="text-sm">
                        Request Meter Data
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`site-request-bills-${site.id}`}
                        checked={site.requestBills}
                        onChange={(e) => updateField('requestBills', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`site-request-bills-${site.id}`} className="text-sm">
                        Request Bills
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`site-acquire-ced-${site.id}`}
                        checked={site.acquireCed}
                        onChange={(e) => updateField('acquireCed', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`site-acquire-ced-${site.id}`} className="text-sm">
                        Acquire CED
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`site-green-power-${site.id}`}
                        checked={site.greenPower}
                        onChange={(e) => updateField('greenPower', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`site-green-power-${site.id}`} className="text-sm">
                        Green Power
                      </Label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <Label htmlFor={`site-dma-provider-${site.id}`} className="text-sm font-medium">
                        DMA Provider
                      </Label>
                      <Select
                        value={site.dmaProvider || ''}
                        onValueChange={(value) => updateField('dmaProvider', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select DMA provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {DMA_PROVIDERS.map((provider) => (
                            <SelectItem key={provider} value={provider}>
                              {provider}
                            </SelectItem>
                          ))}
                          {site.dmaProvider && !DMA_PROVIDERS.includes(site.dmaProvider) && (
                            <SelectItem key={site.dmaProvider} value={site.dmaProvider}>
                              {site.dmaProvider}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Site Documents Upload Section */}
                  <div className="pt-4 border-t">
                    <Label className="text-sm font-medium mb-2 block">
                      Site Documents
                    </Label>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload site-specific documents. All file types accepted.
                    </p>

                    {/* File Upload Area */}
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4 hover:border-primary/60 transition-colors"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('border-primary', 'bg-primary/5');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                        const files = e.dataTransfer.files;
                        if (files && files.length > 0) {
                          handleFileUpload(files);
                        }
                      }}
                    >
                      <input
                        type="file"
                        id={`site-documents-${site.id}`}
                        multiple
                        accept="*/*"
                        onChange={(e) => handleFileUpload(e.target.files)}
                        className="hidden"
                      />
                      <label
                        htmlFor={`site-documents-${site.id}`}
                        className="flex flex-col items-center justify-center cursor-pointer"
                      >
                        <Upload className="w-6 h-6 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {uploadingFiles.length > 0
                            ? `Uploading ${uploadingFiles.length} file(s)...`
                            : 'Click to upload or drag and drop files'}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          PDF, Word, Excel, images and more
                        </span>
                      </label>
                    </div>

                    {/* Uploaded Files List */}
                    {site.documentFilePaths && site.documentFilePaths.length > 0 && (
                      <div className="space-y-2">
                        {site.documentFilePaths.map((filePath, idx) => {
                          const fileName = getFileName(filePath);
                          const isDeleting = deletingFiles.includes(filePath);
                          const fileUrl = getSiteDocumentUrl(filePath);

                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary hover:underline truncate"
                                  title={fileName}
                                >
                                  {fileName}
                                </a>
                              </div>
                              <button
                                onClick={() => handleFileDelete(filePath)}
                                disabled={isDeleting}
                                className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                title="Delete file"
                              >
                                {isDeleting ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Retailer Notes Section */}
                  <div className="pt-4 border-t">
                    <Label htmlFor={`site-retailer-notes-${site.id}`} className="text-sm font-medium mb-2 block">
                      Retailer Notes
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add any notes, special requirements, or instructions for retailers regarding this site.
                    </p>
                    <Textarea
                      id={`site-retailer-notes-${site.id}`}
                      value={site.retailerNotes || ''}
                      onChange={(e) => updateField('retailerNotes', e.target.value)}
                      onBlur={async (e) => {
                        const notesValue = e.target.value.trim() || null;
                        const globalSiteId = site.siteId;
                        if (globalSiteId && !globalSiteId.startsWith('site_')) {
                          try {
                            const updateResult = await updateSite(globalSiteId, { retailerNotes: notesValue });
                            if (!updateResult.success) {
                              console.error('❌ Failed to update site retailer notes:', updateResult.error);
                            }
                          } catch (error) {
                            console.error('❌ Error updating site retailer notes:', error);
                          }
                        }
                        // If no siteId yet, notes are included when site is first saved via saveSitesToGlobalAndLink
                      }}
                      placeholder="e.g., Special requirements, preferred contact method, site access instructions..."
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      )}
    </Card>
  );
};

export default SiteCard;
